/**
 * Document async action hooks for document state management
 *
 * These hooks dispatch document actions directly to the Zustand store via DocumentStore.
 * The DocumentStore handles:
 * 1. Injecting currentUser into actions
 * 2. Forwarding actions to the Zustand store's reduce() method
 *
 * The Zustand store's reduce() method notifies action listeners (ActionRecordService,
 * DocumentSaveService, PatchHandlerService) so middleware functionality is preserved.
 */
import {
    UnknownError,
    addPresetPropertiesToDocumentData,
    getProjectExpressionEvaluationContext,
    shouldMigrateNetsOnDocumentLoad,
    updateDocumentNetNodes,
} from "@buildwithflux/core";
import type {ControlPositionData, IDocumentConfigDataWithoutIds} from "@buildwithflux/core";
import type {IBaseDocumentData, IDocumentData} from "@buildwithflux/models";
import {OutOfRangeErrorCode} from "@buildwithflux/repositories";
import {areWeTestingWithJest} from "@buildwithflux/shared";
import {useCallback} from "react";

import {defaultErrorMessage} from "components/pages/authentication/RouteWithErrorBoundary";
import {getDocumentRetryKey} from "helpers/dataHelper";
import {isMobileDevice} from "helpers/platform";
import type {FluxServices} from "injection/container";
import {useFluxServices} from "injection/hooks";
import {AppStatus, DocumentStatusStates, StorageError} from "modules/app/state/app";
import type {DocumentStore} from "modules/document/state/document";
import {resetPcbDocumentStateInWebWorker} from "modules/pcb_layout_engine/pcbLayoutEngineInMain";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";
import type {PcbBakedGoodsManager} from "modules/stores/pcb/PcbBakedGoodsManager";
import type {UserCodeRuntimeWrapper} from "modules/usercode_runtime/UserCodeRuntimeWrapper";

/**
 * Hook to get the rebakePcbOnDemand action
 *
 * Rebake PCB on demand - used when mobile user clicks "Load PCB Anyway" button
 * to trigger the PCB baking that was skipped during initial document load.
 * After the initial load completes, suppressBaking is set to false to allow
 * incremental baking operations to proceed normally.
 *
 * @returns A function to trigger PCB rebaking on demand
 */
export function useRebakePcbOnDemand(): () => void {
    const {pcbBakedGoodsManager, documentStore} = useFluxServices();

    return useCallback(() => {
        void rebakePcbOnDemand(pcbBakedGoodsManager, documentStore);
    }, [pcbBakedGoodsManager, documentStore]);
}

/**
 * Rebake PCB on demand - used when mobile user clicks "Load PCB Anyway" button
 * to trigger the PCB baking that was skipped during initial document load.
 * After the initial load completes, suppressBaking is set to false to allow
 * incremental baking operations to proceed normally.
 *
 * This is the core async function that can be called from both React hooks
 * and non-React contexts.
 */
async function rebakePcbOnDemand(
    pcbBakedGoodsManager: PcbBakedGoodsManager,
    documentStore: DocumentStore,
): Promise<void> {
    if (areWeTestingWithJest()) {
        return;
    }

    // Get document from Zustand store
    const documentData = documentStore.getState().document;

    if (!documentData) {
        return;
    }

    try {
        await pcbBakedGoodsManager.firstLoad(documentData, documentData.pcbLayoutNodes, documentData.pcbLayoutRuleSets);
        // After successful initial load, allow incremental baking to proceed
        pcbBakedGoodsManager.setSuppressBaking(false);
    } catch (error) {
        // On error, keep suppressBaking true to avoid partial state issues
        FluxLogger.captureError(new UnknownError(`rebakePcbOnDemand error: ${error}`, error));
    }
}

/**
 * Hook to get the setDocumentConfigs action
 *
 * Dispatches directly to Zustand via DocumentStore, bypassing Redux.
 * This action sets document configuration values (viewport state, etc.).
 *
 * @returns A function to set document configs
 */
export function useSetDocumentConfigs(): (configs: IDocumentConfigDataWithoutIds[]) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (configs: IDocumentConfigDataWithoutIds[]) => {
            documentStore.getState().setDocumentConfigs(configs);
        },
        [documentStore],
    );
}

/**
 * Hook to get the setSimulationControlsValue action
 *
 * Dispatches directly to Zustand via DocumentStore, bypassing Redux.
 * This action sets the value of a simulation control for an element.
 *
 * @returns A function to set simulation control values
 */
export function useSetSimulationControlsValue(): (
    elementUid: string,
    controlUid: string,
    value: ControlPositionData,
) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (elementUid: string, controlUid: string, value: ControlPositionData) => {
            documentStore.getState().setSimulationControlsValue(elementUid, controlUid, value);
        },
        [documentStore],
    );
}

/**
 * Factory function to create document async actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch document async actions.
 *
 * @param documentStore - The DocumentStore from FluxServices
 * @param pcbBakedGoodsManager - The PcbBakedGoodsManager from FluxServices (for rebakePcbOnDemand)
 * @param documentStore - The DocumentStore from FluxServices (for rebakePcbOnDemand)
 * @returns An object with document async action methods
 */
export function createDocumentAsyncActions(documentStore: DocumentStore, pcbBakedGoodsManager: PcbBakedGoodsManager) {
    return {
        rebakePcbOnDemand: () => {
            void rebakePcbOnDemand(pcbBakedGoodsManager, documentStore);
        },
        setDocumentConfigs: (configs: IDocumentConfigDataWithoutIds[]) => {
            documentStore.getState().setDocumentConfigs(configs);
        },
        setSimulationControlsValue: (elementUid: string, controlUid: string, value: ControlPositionData) => {
            documentStore.getState().setSimulationControlsValue(elementUid, controlUid, value);
        },
    };
}

const LOAD_RETRY_MAX = 1;
const LOAD_RETRY_DELAY = 3000;

/**
 * Load a document into the Zustand store
 *
 * This is the main document loading function that:
 * 1. Validates the base document data
 * 2. Loads the full document data from storage
 * 3. Initializes services (PCB baking, comments, simulator)
 * 4. Dispatches the document data to the Zustand store
 *
 * This function replaces the Redux thunk `documentThunks.loadDocument`.
 *
 * @param baseDocument - The base document data (metadata) to load
 * @param services - The FluxServices container with all required services
 */
export async function loadDocument(baseDocument: IBaseDocumentData, services: FluxServices): Promise<void> {
    if (!baseDocument) {
        // NOTE: this should never happen
        FluxLogger.captureError(new Error(`loadDocument called without a baseDocument`));
        // Sets the full page error and sets app status to
        // noDocumentData because something critical went wrong
        services.appStore.getState().setError(new StorageError(defaultErrorMessage));
        return;
    }

    /**
     * If the doc is already loaded, dont do anything.
     *
     * This is happening when navigating between projects using browser back/forward...
     *
     * Somehow the useEffect calling `loadDocument` is triggered twice
     * in this scenario. A direct visit has no such issue.
     *
     * TODO: Figure out why that useEffect does not work as expected
     */
    // Get document from Zustand store
    const document = services.documentStore.getState().document;

    if (document && baseDocument.uid === document.uid) {
        // eslint-disable-next-line no-console
        console.debug(`loadDocument: document data for ${document.slug} already loaded`);
        return;
    }

    // Set up Sentry tags
    FluxLogger.setDocumentUid(baseDocument.uid, baseDocument.owner_handle + "/" + baseDocument.slug);

    const {
        documentStorage,
        documentService,
        pcbBakedGoodsManager,
        algoliaConnector,
        userCodeRuntimeWrapper,
        useCommentStore,
        currentUserService,
        useUserUiStateStore,
        useFeatureFlagsStore,
    } = services;

    try {
        // Update Zustand app store
        services.appStore.getState().setAppStatus(AppStatus.loadingDocument);

        // await for document data from backend... this is the big one
        let documentData = await documentStorage.loadDocument(
            baseDocument.uid,
            currentUserService.getCurrentUser()?.uid,
        );

        // NOTE: it is important to get the app state after the
        // documentStorage.loadDocument call in case of a concurrent nagivation
        const currentBaseDocumentUid = services.useDocumentMetaStore.getState().baseDocument?.uid;

        // Corner case #1: You go to a projects/ID URL like
        // https://flux.ai/projects/f3cbdbc4-3f0d-49ca-bf94-3ba21e73a086
        // . The app will redirect to a canonical URL like
        // https://flux.ai/vasy_skral/tl3472idr, which unmounts the
        // Document component, does clearApp and then later triggers
        // another load of the same data. See useDocumentMeta.
        if (currentBaseDocumentUid === undefined) {
            // eslint-disable-next-line no-console
            console.debug(
                "loadDocument: base document was cleared during the load request. Has a second request been made? Cancelling the first request.",
            );
            return;
        }
        // Corner case #2: You load a project A with a slow network, before
        // the data comes back, you go back to another project B.
        // Because we never reload the app, we actually fired two base document load
        // requests. See useDocumentMeta. When the first request (for project A)
        // comes back, we should not use that data to init the app. This
        // is done on the assumption that we always load uid to
        // documentMeta first before calling this loadDocument function.
        else if (currentBaseDocumentUid !== documentData.uid) {
            // eslint-disable-next-line no-console
            console.debug(
                `loadDocument: requested document ${documentData.uid} changed during load to ${currentBaseDocumentUid}. Has a second request been made? Cancelling the first request.`,
            );
            return;
        }

        // Clear document data in document service
        // Do NOT clear baseDocumentData beforeLoad.
        clearApp(services);

        // Clear up retry # because we successfully loaded the doc. See
        // handleDocumentVersionError
        sessionStorage.removeItem(getDocumentRetryKey(baseDocument.uid));

        // Load user ui state first, we load this before setting the
        // document data because loading document data is an optimistic load,
        // meaning we are showing the full ui while loading document
        // data, and we want the ui to be ready asap
        await useUserUiStateStore.getState().loadCurrentUserUiState(baseDocument.uid);

        // Re-initialize document service, make its scope to this new document
        documentService.onDocumentLoad(documentData);

        /**
         * HACK: Recalculate the impedance properties of pcb layout
         * nodes on document load, sub-document terminals, and anything
         * else that may need to be re-derived for net instances or net
         * nodes.
         *
         * We only do this upgrade for new documents after the net
         * migration. See detail in `shouldMigrateNetsOnDocumentLoad`
         * function
         *
         * QUESTION: why not do a proper migration instead? do we need
         * this until the end of time?
         *
         * TODO: revisit this after snapshots are fully deployed...
         * which is now!
         *
         * TODO this also ensures net consistency, due to to-be-fixed bugs
         * we have with syncing wire nets with document.nets / pcb nets,
         * and the need to fix existing documents with corrupt nets.
         * We decided not to make a migration for that, because we're not
         * yet certain we've found all the sources, and so don't want it
         * to be a one-off update.
         * Anyway, if you remove this hack, make sure you either call
         * ensureNetAndPcbNodesConsistency() here or have a better plan.
         */
        if (shouldMigrateNetsOnDocumentLoad(documentData)) {
            try {
                updateDocumentNetNodes(documentData, FluxLogger);
            } catch (error) {
                // Ignore migration error and hope for the best. The net
                // nodes are all derived data, and the error from
                // updateNetNodes will most likely resurface during user
                // interactions when we can deal with it better.
                FluxLogger.captureError(new UnknownError(`loadDocument handled error: ${error}`, error));
            }
        }

        // Adds system-default properties (presets) to the document data if they don't already exist
        // NOTE: in certain contexts, `documentData` is immutable, so we need to re-assign it to a mutated copy
        documentData = addPresetPropertiesToDocumentData(documentData);

        // NOTE: setDocumentData action is not handled withHistory so no
        // latestPatch and it will not trigger a full bake so we need
        // firstLoad
        // Dispatch setDocumentData directly to Zustand
        services.documentStore.getState().setDocumentData(documentData);
        // Update Zustand app store
        services.appStore.getState().setAppStatus(AppStatus.documentReady);

        // We skip the following initializations in jest environment
        // because they need web workers or they have unhandled promises.
        // NOTE: initializations are in rough priority order
        if (!areWeTestingWithJest()) {
            try {
                // Notify algolia connector when the current document changes so we can get a new scoped public key if necessary.
                void algoliaConnector.setAuthContext({activeDocumentUid: documentData.uid});

                // QUESTION: is this TODO still relevant? typesense has launched
                // TODO: Hook up typesense connector here

                // Rebake the entire board on first load (skip on mobile when feature flag is enabled)
                const mobileDontLoadEditors = useFeatureFlagsStore.getState().mobileDontLoadEditors ?? false;
                const shouldSkipPcbBaking = isMobileDevice() && mobileDontLoadEditors;

                // Set suppressBaking state based on mobile + feature flag condition
                // When true, incremental baking operations will short-circuit until user clicks "Load PCB Anyway"
                pcbBakedGoodsManager.setSuppressBaking(shouldSkipPcbBaking);

                if (!shouldSkipPcbBaking) {
                    void pcbBakedGoodsManager.firstLoad(
                        documentData,
                        documentData.pcbLayoutNodes,
                        documentData.pcbLayoutRuleSets,
                    );
                }

                void initCommentStore(baseDocument.uid, useCommentStore);

                void initSimulator(userCodeRuntimeWrapper, documentData);
            } catch (error) {
                // Ignore errors in the above initializations and hope
                // for the best. Nodes are derived data, comments and
                // simulator are not critical, and any error will likely
                // resurface during user interactions where we can deal
                // with it better.
                FluxLogger.captureError(new UnknownError(`loadDocument handled error: ${error}`, error));
            }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
        if (error.code === OutOfRangeErrorCode) {
            // NOTE: thrown by getDocumentFromFirebase
            handleDocumentVersionError(baseDocument.uid, error);
        } else {
            FluxLogger.captureError(new UnknownError(`loadDocument unhandled error: ${error}`, error));
        }

        // Sets the full page error and sets app status to
        // noDocumentData because something critical went wrong
        const errorToSet = error.code ? error : new StorageError(defaultErrorMessage);
        // Update Zustand app store (setError also sets appStatus to noDocumentData)
        services.appStore.getState().setError(errorToSet);
    }
}

/**
 * Clear the document state from the Zustand store
 *
 * This function clears all document-related state and disposes of services.
 * It should be called when unmounting the Document component to ensure
 * a clean slate when opening the next document.
 *
 * This function replaces the Redux thunk `documentThunks.clear`.
 *
 * @param services - The FluxServices container with all required services
 */
export function clearDocument(services: FluxServices): void {
    const {userCodeRuntimeWrapper, useDocumentMetaStore} = services;

    // Clear Zustand documentMeta store
    const documentMetaStore = useDocumentMetaStore.getState();
    documentMetaStore.setBaseDocumentData(undefined);
    documentMetaStore.setDocumentOwner(undefined);
    documentMetaStore.setDocumentOrganizationOwner(undefined);

    clearApp(services);

    // NOTE: Don't clear simulator on document load, or else 2 instances get created
    userCodeRuntimeWrapper.dispose();
}

/**
 * Initialize the comment store for a document
 */
function initCommentStore(documentUid: string, useCommentStore: FluxServices["useCommentStore"]): void {
    useCommentStore.getState().prefetch(documentUid);
}

/**
 * Initialize the simulator for a document
 */
async function initSimulator(
    userCodeRuntimeWrapper: UserCodeRuntimeWrapper,
    documentData: IDocumentData,
): Promise<void> {
    const simulator = await userCodeRuntimeWrapper.init();
    await simulator.setDocumentData(documentData);

    // this function can only run in browser window context...so need to run it here
    const projectExpressionEvaluationContext = getProjectExpressionEvaluationContext(documentData);
    await simulator.setProjectExpressionContext(projectExpressionEvaluationContext);
}

/**
 * Clear app state (internal helper)
 */
function clearApp(services: FluxServices): void {
    const {
        pcbBakedGoodsManager,
        useCommentStore,
        useUserUiStateStore,
        autoLayoutService,
        useReviewStore,
        useCopilotChatStore,
        changeHistoryStore,
        appStore,
    } = services;
    useCommentStore.getState().shutdown();
    useUserUiStateStore.getState().clear();
    useReviewStore.getState().clear();

    // Clean up auto layout, reset state machine, clean up any subscriptions
    // New infra:
    autoLayoutService.shutdown();

    pcbBakedGoodsManager.clear();

    useCopilotChatStore.getState().clear();

    if (!areWeTestingWithJest()) {
        resetPcbDocumentStateInWebWorker();
    }
    // Dispatch clearDocumentData directly to Zustand
    // Note: clearDocumentData doesn't need currentUser injection, so we call reduce() directly
    services.documentStore.getState().clearDocumentData();
    // Update Zustand app store
    appStore.getState().setDocumentStatus(DocumentStatusStates.none, new Date().getTime());

    // Clear the Zustand changeHistory store
    changeHistoryStore.getState().clearChangeHistory();
}

/**
 * Handle document version error with retry logic
 */
function handleDocumentVersionError(documentUid: string, error: unknown): void {
    const documentRetryKey = getDocumentRetryKey(documentUid);
    // note - we need to make sure that schemaVersion is updated in both server and client
    // side. Otherwise, this could result in infinite loading loop
    const count = sessionStorage.getItem(documentRetryKey);
    let retryCounter = count ? parseInt(count, 10) : 0;
    retryCounter += 1;

    if (retryCounter <= LOAD_RETRY_MAX) {
        sessionStorage.setItem(documentRetryKey, `${retryCounter}`);
        setTimeout(() => window.location.reload(), LOAD_RETRY_DELAY);
    } else {
        sessionStorage.removeItem(documentRetryKey);
        FluxLogger.captureError(
            new UnknownError(`loadDocument failed from a document version out-of-range error: ${error}`, error),
        );
    }
}
